import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Global search bar for the top nav (PR #39). Always visible on every page for
 * admins, brand users, and anonymous visitors. Debounced 200ms; results are
 * grouped into three tiers (chains / retail locations / other licenses) and
 * navigated via hash href. ⌘K / Ctrl-K focuses; Esc closes; ↑/↓ + Enter pick.
 */

type SearchResult = {
  type: "chain" | "retail_license" | "other_license";
  id: string;
  title: string;
  subtitle: string;
  href: string;
  is_my_customer?: boolean;
  match_score: number;
};

type SearchResponse = {
  query: string;
  results: SearchResult[];
  tier_counts: { chain: number; retail_license: number; other_license: number };
};

const TIER_LABELS: Record<SearchResult["type"], string> = {
  chain: "Chains",
  retail_license: "Retail locations",
  other_license: "Other licenses",
};

const TIER_ORDER: SearchResult["type"][] = ["chain", "retail_license", "other_license"];

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const reqSeq = useRef(0);

  const runSearch = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      // Auth (if any) rides on the admin_session / storesync_session cookie via
      // credentials:"include"; role only affects Customer-Master pinning.
      const res = await fetch(
        `/api/search?q=${encodeURIComponent(trimmed)}&limit=25`,
        { credentials: "include" }
      );
      if (seq !== reqSeq.current) return; // a newer request superseded this one
      if (!res.ok) {
        setResults([]);
        return;
      }
      const body = (await res.json()) as SearchResponse;
      if (seq !== reqSeq.current) return;
      setResults(body.results);
      setActive(0);
    } catch {
      if (seq === reqSeq.current) setResults([]);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  // Debounce the input by 200ms.
  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 200);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  // ⌘K / Ctrl-K focuses the input from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r: SearchResult) => {
    window.location.hash = r.href;
    setOpen(false);
    setQ("");
    setResults([]);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[active];
      if (r) go(r);
    }
  };

  const showDropdown = open && q.trim().length >= 2;
  const grouped = TIER_ORDER.map((type) => ({
    type,
    rows: results.filter((r) => r.type === type),
  })).filter((g) => g.rows.length > 0);

  // Flat index so keyboard highlight maps across grouped sections.
  let flatIndex = -1;

  return (
    <div ref={containerRef} className="relative w-full max-w-xs">
      <div className="relative">
        <svg
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
          fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={q}
          placeholder="Search chains, licenses…"
          aria-label="Global search"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="block w-full rounded-md border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-500/40 focus:outline-none pl-8 pr-8 py-1.5"
        />
        {loading && (
          <svg
            className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400"
            viewBox="0 0 24 24" fill="none"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
          </svg>
        )}
        {!loading && (
          <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline text-[10px] font-medium text-gray-400 border border-gray-200 rounded px-1 py-px">
            ⌘K
          </kbd>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-40 mt-1 w-[28rem] max-w-[80vw] rounded-lg border border-gray-200 bg-white shadow-lg max-h-[70vh] overflow-auto">
          {grouped.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500">
              {loading ? "Searching…" : `No matches for "${q.trim()}"`}
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.type} className="py-1">
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {TIER_LABELS[group.type]}
                </div>
                {group.rows.map((r) => {
                  flatIndex += 1;
                  const idx = flatIndex;
                  const isActive = idx === active;
                  return (
                    <button
                      key={`${r.type}:${r.id}`}
                      type="button"
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(r)}
                      className={`flex w-full items-start gap-2 px-3 py-2 text-left ${isActive ? "bg-accent-50" : "hover:bg-gray-50"}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-gray-900">{r.title}</span>
                          {r.is_my_customer && (
                            <span className="shrink-0 inline-flex items-center rounded-full bg-accent-100 px-1.5 py-px text-[10px] font-semibold text-accent-700">
                              ★ My customer
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs text-gray-500">{r.subtitle}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
